import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BedDouble, Plane } from "lucide-react";
import { PageWidth } from "./components/travel-ui";
import { formatLongDate, formatReleaseDate } from "./lib/format";
import {
  cancelTrip,
  getTrips,
  type MemberTrip,
  type TripStatus,
} from "./services/trip.service";

const TABS: TripStatus[] = ["Upcoming", "Completed", "Cancelled"];

export default function MemberTrips() {
  const [trips, setTrips] = useState<MemberTrip[]>([]);
  const [tab, setTab] = useState<TripStatus>("Upcoming");
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let active = true;
    const sync = () => {
      void getTrips()
        .then((rows) => {
          if (active) setTrips(rows);
        })
        .catch((err: unknown) => {
          if (active) setLoadError(err instanceof Error ? err.message : "Could not load trips.");
        });
    };
    sync();
    window.addEventListener("member-trips", sync);
    return () => {
      active = false;
      window.removeEventListener("member-trips", sync);
    };
  }, []);

  const visible = trips
    .filter((trip) => trip.status === tab)
    .sort((a, b) => b.startDate.localeCompare(a.startDate));

  return (
    <PageWidth>
      <h1 className="text-2xl font-bold">Your trips</h1>
      <p className="mt-1 text-sm text-[#595959]">
        Stays and flights you have booked, including cash and points used.
      </p>
      <div className="mt-4 flex gap-2">
        {TABS.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => setTab(item)}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold ${
              tab === item
                ? "bg-[#003580] text-white"
                : "bg-white text-[#1a1a1a] ring-1 ring-[#e7e7e7]"
            }`}
          >
            {item}
          </button>
        ))}
      </div>

      {loadError ? <p className="mt-4 text-sm text-red-700">{loadError}</p> : null}
      <div className="mt-5 space-y-3">
        {visible.length === 0 ? (
          <div className="rounded-lg border border-[#e7e7e7] bg-white p-8 text-center">
            <p className="font-semibold">No {tab.toLowerCase()} trips.</p>
            <Link
              to="/member"
              className="mt-2 inline-block text-sm font-semibold text-[#006ce4]"
            >
              Book a stay or flight
            </Link>
          </div>
        ) : (
          visible.map((trip) => (
            <article
              key={trip.id}
              className="grid gap-4 rounded-lg border border-[#e7e7e7] bg-white p-4 sm:grid-cols-[120px_1fr]"
            >
              {trip.image ? (
                <img
                  src={trip.image}
                  alt=""
                  className="h-24 w-full rounded-md object-cover"
                />
              ) : (
                <div className="flex h-24 items-center justify-center rounded-md bg-[#003580] text-white">
                  {trip.kind === "flight" ? (
                    <Plane className="h-6 w-6" />
                  ) : (
                    <BedDouble className="h-6 w-6" />
                  )}
                </div>
              )}
              <div className="flex flex-col gap-2 sm:flex-row sm:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#595959]">
                    {trip.kind === "flight" ? "Flight" : "Stay"} · {trip.reference}
                  </p>
                  <h2 className="text-lg font-bold">{trip.title}</h2>
                  <p className="text-sm text-[#595959]">{trip.subtitle}</p>
                  <p className="mt-1 text-sm">
                    {formatLongDate(trip.startDate)}
                    {trip.endDate !== trip.startDate
                      ? ` – ${formatLongDate(trip.endDate)}`
                      : ""}
                  </p>
                  <p className="text-sm text-[#595959]">{trip.detail}</p>
                </div>
                <div className="text-sm sm:text-right">
                  <p className="font-bold">
                    {new Intl.NumberFormat("en-IN", { style: "currency", currency: trip.currency, maximumFractionDigits: 2 }).format(trip.cashPaid)} cash
                  </p>
                  <p className="text-[#595959]">
                    {trip.pointsUsed.toLocaleString("en-IN")} pts used
                  </p>
                  <p className={trip.pointsPending ? "text-[#9a6700]" : "text-[#008009]"}>
                    +{trip.pointsEarned.toLocaleString("en-IN")} pts earned
                    {trip.pointsPending
                      ? ` · pending${formatReleaseDate(trip.pointsAvailableAt) ? ` until ${formatReleaseDate(trip.pointsAvailableAt)}` : ""}`
                      : ""}
                  </p>
                  {trip.status === "Upcoming" ? (
                    <button
                      type="button"
                      className="mt-2 text-sm font-semibold text-red-700"
                      onClick={() => {
                        void cancelTrip(trip.id)
                          .then(() => getTrips().then(setTrips))
                          .catch((err: unknown) => setLoadError(err instanceof Error ? err.message : "Could not cancel."));
                      }}
                    >
                      Cancel booking
                    </button>
                  ) : null}
                </div>
              </div>
            </article>
          ))
        )}
      </div>
    </PageWidth>
  );
}
